<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { api } from "@/api/client";
import AgentFilterBar from "@/components/AgentFilterBar.vue";
import { useAgentFilter } from "@/composables/useAgentFilter";
import { describeError } from "@/utils/errors";
import type { AgentView, GlobalTunnel } from "@/api/types";

const { t } = useI18n();

const agents = ref<AgentView[]>([]);
const tunnels = ref<GlobalTunnel[]>([]);
const loading = ref(true);
const listError = ref<string | null>(null);

// create form
const host = ref(""); // agent A (host the forward lives on)
const target = ref(""); // agent B (relay target)
const remotePort = ref(22);
const listenAddr = ref(""); // blank → server default 127.0.0.1:2222 (+ port walk)
const submitting = ref(false);
const formError = ref<string | null>(null);
const copiedCmdFor = ref<string | null>(null);

function errorMessage(err: unknown): string {
  return describeError(err, t);
}

function agentLabel(id: string): string {
  const a = agents.value.find((x) => x.id === id);
  return a ? a.display_name || a.hostname || a.login : id.slice(0, 8);
}

// Picker filter (search/tags/online) narrows both selects.
const filter = useAgentFilter(agents);
/** Agents that can host a tunnel: online + advertise `tunnel_host`. */
const tunnelHosts = computed(() =>
  filter.filtered.filter((a) => (a.capabilities ?? []).includes("tunnel_host")),
);
/** Valid relay targets B (relay opted-in), excluding the chosen host A. */
const relayTargets = computed(() =>
  filter.filtered.filter((a) => a.relay_enabled && a.id !== host.value),
);

async function loadAgents() {
  try {
    agents.value = (await api.agents.list()).agents;
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

async function loadTunnels() {
  listError.value = null;
  try {
    tunnels.value = (await api.tunnels.listAll()).tunnels;
  } catch (err) {
    listError.value = errorMessage(err);
  } finally {
    loading.value = false;
  }
}

async function refresh() {
  await Promise.all([loadAgents(), loadTunnels()]);
}

async function createTunnel() {
  if (!host.value || !target.value) return;
  formError.value = null;
  submitting.value = true;
  try {
    await api.agents.tunnels.create(host.value, {
      target_agent_id: target.value,
      remote_port: remotePort.value,
      listen_addr: listenAddr.value.trim() || undefined,
    });
    listenAddr.value = "";
    await loadTunnels();
  } catch (err) {
    formError.value = errorMessage(err);
  } finally {
    submitting.value = false;
  }
}

async function stopTunnel(tn: GlobalTunnel) {
  try {
    await api.agents.tunnels.stop(tn.host_agent_id, tn.tunnel_id);
    await loadTunnels();
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

/** Operator-side command to reach B from your own machine. Server/token come
 * from the operator's local `nyx-operator login`, so no secret is embedded. */
function nyxOperatorCommand(targetId: string): string {
  return `nyx-operator tunnel ${targetId} --port 22 --listen 127.0.0.1:2222`;
}

async function copyOperatorCommand() {
  if (!target.value) return;
  const cmd = nyxOperatorCommand(target.value);
  try {
    await navigator.clipboard.writeText(cmd);
    copiedCmdFor.value = target.value;
    setTimeout(() => {
      if (copiedCmdFor.value === target.value) copiedCmdFor.value = null;
    }, 2000);
  } catch {
    window.prompt(t("tunnels.copy_fallback"), cmd);
  }
}

onMounted(refresh);
</script>

<template>
  <div class="admin-page">
    <div class="admin-page__header">
      <h1>{{ t("tunnels.title") }}</h1>
      <button type="button" @click="refresh">{{ t("tunnels.refresh") }}</button>
    </div>

    <section class="admin-card">
      <h2 style="margin-top: 0; font-size: 1rem">{{ t("tunnels.create_title") }}</h2>
      <p style="margin: 0 0 0.75rem; opacity: 0.8; font-size: 0.85rem">{{ t("tunnels.hint") }}</p>
      <AgentFilterBar v-if="agents.length > 1" :filter="filter" />
      <form class="admin-form" @submit.prevent="createTunnel">
        <label>
          <span>{{ t("tunnels.host") }}</span>
          <select v-model="host" :disabled="submitting">
            <option value="">—</option>
            <option v-for="a in tunnelHosts" :key="a.id" :value="a.id">
              {{ agentLabel(a.id) }}
            </option>
          </select>
        </label>
        <label>
          <span>{{ t("tunnels.target") }}</span>
          <select v-model="target" :disabled="submitting || !host">
            <option value="">—</option>
            <option v-for="a in relayTargets" :key="a.id" :value="a.id">
              {{ agentLabel(a.id) }}
            </option>
          </select>
        </label>
        <label>
          <span>{{ t("tunnels.remote_port") }}</span>
          <input
            v-model.number="remotePort"
            type="number"
            min="1"
            max="65535"
            :disabled="submitting"
          />
        </label>
        <label>
          <span>{{ t("tunnels.listen") }}</span>
          <input v-model="listenAddr" placeholder="127.0.0.1:2222" :disabled="submitting" />
        </label>
        <button type="submit" class="admin-form__submit" :disabled="submitting || !host || !target">
          {{ submitting ? t("admin.submitting") : t("tunnels.create") }}
        </button>
        <button
          v-if="target"
          type="button"
          :title="t('tunnels.copy_hint')"
          @click="copyOperatorCommand"
        >
          {{ copiedCmdFor === target ? t("tunnels.copied") : t("tunnels.copy_op") }}
        </button>
      </form>
      <p v-if="formError" class="admin-error" style="margin-top: 0.5rem">{{ formError }}</p>
    </section>

    <section class="admin-card">
      <h2 style="margin-top: 0; font-size: 1rem">{{ t("tunnels.open_title") }}</h2>
      <p v-if="loading" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="listError" class="admin-error">{{ listError }}</p>
      <p v-else-if="tunnels.length === 0" class="admin-empty">{{ t("tunnels.none") }}</p>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>{{ t("tunnels.host") }}</th>
            <th>{{ t("tunnels.target") }}</th>
            <th>{{ t("tunnels.remote_port") }}</th>
            <th>{{ t("tunnels.local") }}</th>
            <th>{{ t("tunnels.conns") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tn in tunnels" :key="tn.host_agent_id + tn.tunnel_id">
            <td :data-label="t('tunnels.host')">{{ agentLabel(tn.host_agent_id) }}</td>
            <td :data-label="t('tunnels.target')">{{ agentLabel(tn.target_agent_id) }}</td>
            <td :data-label="t('tunnels.remote_port')">{{ tn.remote_port }}</td>
            <td :data-label="t('tunnels.local')">{{ tn.local_addr }}</td>
            <td :data-label="t('tunnels.conns')">{{ tn.connections }}</td>
            <td class="admin-table__actions">
              <button type="button" @click="stopTunnel(tn)">{{ t("tunnels.stop") }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>
